'use client';

/**
 * Spec 81 follow-on — "get started with one prompt" affordance.
 *
 * A copyable one-liner the visitor can paste into any MCP-capable
 * agent. Renders the prompt in monospace inside a bordered pill with a
 * trailing copy button; a visually-hidden live region announces the
 * result to screen readers.
 *
 * Motion — the copy button uses the transitions.dev "icon swap"
 * pattern (https://transitions.dev, `09-icon-swap`): both glyphs stay
 * mounted, stacked in the SAME grid cell, and cross-fade with a blur +
 * scale rather than being swapped in and out of the DOM. Ours is the
 * pure-Tailwind translation of that snippet — this repo is Tailwind-v4
 * tokens only, so the upstream `t-*` classes and `:root` custom
 * properties become utilities and the `prefers-reduced-motion` guard
 * becomes the `motion-reduce:` variant. The upstream tuning is
 * preserved verbatim: 250ms / ease-in-out / blur 2px / start-scale
 * 0.25.
 *
 * The icon colour is deliberately ASYMMETRIC: it snaps to a strong
 * green (`icon-success`, jade-500) the instant the copy lands, then
 * decays back to the default over a full second. A slow ramp *in*
 * would read as lag; a slow fade *out* reads as the confirmation
 * settling. The decay is still running when the 1.5s `copied` window
 * closes, so the green drains as the check swaps back to the copy
 * glyph rather than snapping.
 *
 * Stateless from the caller's perspective — pass the `prompt` string;
 * the clipboard interaction is self-contained (hence 'use client').
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { Icon } from './icon.js';

export interface PromptCopyProps {
  /** The one-line prompt to display + copy. */
  prompt: string;
  /**
   * Small caption above the box telling the visitor what to do with
   * the prompt. Pass an empty string to render the box on its own.
   */
  legend?: string;
  /** Accessible label for the copy button. */
  copyLabel?: string;
  /** Applied to the outer legend+box column, not the box itself. */
  className?: string;
}

const DEFAULT_LEGEND = 'To start, copy and paste this into your AI agent.';

/**
 * transitions.dev `.t-icon-swap .t-icon` — both glyphs occupy grid
 * cell 1/1 so they overlap exactly; only opacity/filter/scale animate,
 * all of which are compositor-friendly.
 *
 * The transition list names `scale`, NOT `transform`. Upstream writes
 * `transform: scale(...)`, but Tailwind v4's `scale-*` utilities emit
 * the standalone `scale` property — so a `transition-[…,transform]`
 * here compiles fine, reports `transform: none`, and silently snaps
 * the glyph to full size instead of easing it.
 */
const SWAP_BASE =
  'col-start-1 row-start-1 transition-[opacity,filter,scale] duration-[250ms] ease-in-out motion-reduce:transition-none';
/** Visible half of the swap. */
const SWAP_IN = 'scale-100 opacity-100 blur-0';
/** Hidden half — upstream `--icon-swap-blur` / `--icon-swap-start-scale`. */
const SWAP_OUT = 'scale-[0.25] opacity-0 blur-[2px]';

export function PromptCopy({ prompt, legend = DEFAULT_LEGEND, copyLabel = 'Copy prompt', className }: PromptCopyProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permissions / non-secure context) —
      // the prompt text itself stays selectable, so no hard failure.
    }
  }

  return (
    <div className={cn('flex max-w-full flex-col items-center gap-2', className)}>
      {/* `subheading-1` (jet-400), not `caption-1` (jet-100) — the
          caption token is only 3.27:1 on the page white, under the
          4.5:1 floor for text this size. jet-400 clears 10.7:1 and
          still sits back from the prompt itself. */}
      {legend ? <p className="font-body text-body-sm text-text-subheading-1">{legend}</p> : null}
      <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-border-primary bg-surface-card-primary py-2.5 pr-2.5 pl-5 md:gap-4 md:py-3 md:pr-3 md:pl-6">
        <code className="min-w-0 truncate font-mono text-[13px] leading-6 text-text-body md:text-[15px] lg:text-base">
          {prompt}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copyLabel}
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-full md:size-10',
            'hover:bg-surface-hover-1 active:bg-surface-click-1',
            // See the header note — fast in, slow out.
            'transition-[color] motion-reduce:transition-none',
            copied ? 'text-icon-success duration-75' : 'text-icon-primary duration-1000',
          )}
        >
          {/* Both glyphs stay mounted and cross-fade in place. */}
          <span className="grid">
            <Icon name="copy" size={24} className={cn(SWAP_BASE, copied ? SWAP_OUT : SWAP_IN)} />
            <Icon name="check" size={24} className={cn(SWAP_BASE, copied ? SWAP_IN : SWAP_OUT)} />
          </span>
        </button>
        <span aria-live="polite" className="sr-only">
          {copied ? 'Prompt copied to clipboard' : ''}
        </span>
      </div>
    </div>
  );
}
